// MUI's Select rather than the foundation's, as every other section of this
// panel (Mesh, Rotation, Vector Field) draws its pickers: the sections are
// read as one form, and one control of a different height and style among
// them reads as a mistake. Migrate them together or not at all.
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import classNames from "classnames";
import { isEqual } from "lodash";
import * as React from "react";

import { RobotoTypography } from "@/shared/components/RobotoTypography";
import { pathMatcher } from "@/shared/domain/topics";
import { useVizDispatch } from "@/shared/state/visualization";
import { updateThreeDEntity } from "@/shared/state/visualization/actions";
import {
  Entity,
  PointCloud,
  PointCloudColorMode,
  PointCloudGradientMeasure,
} from "@/shared/state/visualization/schema/v1/three-d";

import {
  FileSelector,
  PathSelector,
} from "../../../controls/DataSourceSelector";
import {
  useDataFiles,
  useFileIdToTopics,
  useWorkspaceFiles,
} from "../../../WorkspaceCtx/hooks";
import { ConfigItem } from "../ConfigItem";

import { AxisPresetPicker } from "./AxisPresetPicker";
import styles from "./EntityConfig.module.css";
import { GradientColorFields } from "./GradientColorFields";
import { isPointCloudDataPath } from "./isPointCloudDataPath";
import {
  axesOf,
  POINT_CLOUD_PRESETS,
  presetIdFor,
  type PointCloudPreset,
} from "./pointCloudPresets";
import {
  buildPointCloudSpec,
  pointCloudInputsFor,
  validateGradientRange,
  validatePointSize,
} from "./pointCloudSpec";
import { joinedPathFor } from "./PositionConfig/positionTopicData";
import { parseNumberInput, validateStride } from "./vectorFieldValidation";

interface Props {
  entity: Entity;
  panelId: string;
}

/** A row of the axis preset picker: a preset, or the axes as they stand. */
type PointCloudPresetOption = Pick<PointCloudPreset, "name"> & {
  id: PointCloudPreset["id"] | "custom";
};

/** Axes that match no preset, bound or scaled by hand. */
const CUSTOM_PRESET_OPTION: PointCloudPresetOption = {
  id: "custom",
  name: { title: "Custom", subtitle: "", formatHint: "" },
};

/** How a cloud can be colored, in the order the settings offer them. */
const COLOR_MODES: readonly { value: PointCloudColorMode; label: string }[] = [
  { value: "auto", label: "Message colors when present" },
  { value: "rgb", label: "Message colors" },
  { value: "flat", label: "Flat color" },
  { value: "gradient", label: "Gradient" },
];

/** What a gradient can color by, in the order the settings offer them. */
const GRADIENT_MEASURES: readonly {
  value: PointCloudGradientMeasure;
  label: string;
}[] = [
  { value: "up", label: "Up" },
  { value: "distance", label: "Distance from center" },
];

/** Parses one end of a gradient's range; blank leaves it to the shown message. */
function parseRangeEnd(
  value: string,
  label: string,
): ReturnType<typeof parseNumberInput> | { ok: true; value: undefined } {
  return value.trim() === ""
    ? { ok: true, value: undefined }
    : parseNumberInput(value, label);
}

/**
 * Binds one PointCloud topic to the entity and sets how it is drawn.
 *
 * The topic is bound through its `data` message path, so the binding is an
 * ordinary `TopicData` that layouts and the file selector already understand.
 * The axis presets are the conventions in `POINT_CLOUD_PRESETS`; the stored
 * axes of a spec matching none of them show as "Custom".
 *
 * The fields hold the form's own copy of the cloud, written back as the user
 * edits them. A cloud set from outside the form — a layout opened over the
 * panel, an undo, another control — is loaded into them instead of being
 * written over, and a cloud that is only opened is never written back.
 * Clearing the topic removes the cloud from the entity: with no topic there is
 * nothing to draw, and a field that reads as empty must not leave the cloud it
 * named drawn and fetching.
 */
export function PointCloudConfig({ entity, panelId }: Props) {
  const dispatch = useVizDispatch();
  const availableFiles = useDataFiles();
  const fileIdToTopics = useFileIdToTopics();

  // The unfiltered workspace list, which the bound file is looked for in:
  // `availableFiles` drops any file whose topics have not arrived yet, so
  // looking there would flag a file that is present as missing. The
  // FileSelector still offers `availableFiles`, since a file with no topics is
  // not a valid pick.
  const workspaceFiles = useWorkspaceFiles();

  // A point cloud records no file of its own; its file is whichever one its
  // source names. That file can be absent from the workspace (a workspace
  // reopened without it), and is then flagged, with nothing scoped to it,
  // until the user picks a file that is present.
  const boundFileId =
    entity.pointCloud?.source.topic.association.association_id;
  const boundFileDangling =
    boundFileId !== undefined &&
    !workspaceFiles.some((file) => file.file_id === boundFileId);

  // The user's last FileSelector pick scopes the topic tree, over the bound
  // file, until a cloud set from outside the form clears it. Before any pick
  // the bound file does; with one file and nothing bound there is nothing to
  // pick.
  const [scopingFile, setScopingFile] = React.useState<string | undefined>(
    undefined,
  );
  const effectiveFileId =
    scopingFile ??
    (boundFileDangling ? undefined : boundFileId) ??
    (availableFiles.length === 1 && !boundFileDangling
      ? availableFiles[0].file_id
      : undefined);
  const topicTree = effectiveFileId
    ? (fileIdToTopics[effectiveFileId] ?? [])
    : [];

  // The cloud this form last loaded into its fields or last dispatched. An
  // `entity.pointCloud` that differs from it was set from outside the form.
  const pointCloudRef = React.useRef(entity.pointCloud);

  // While a dispatch made by the effect below is in flight, the cloud the
  // entity held before it. That dispatch renders after the edit that caused
  // it, so a second keystroke can render first and see an entity that does not
  // hold it yet: not a change from outside, and loading it would drop the
  // keystroke. PositionConfig guards the same race.
  const dispatchedOverRef = React.useRef<
    { cloud: PointCloud | undefined } | undefined
  >(undefined);

  // The topic selector seeds its text once, when it mounts. Bumping this key
  // remounts it to show the topic now bound.
  const topicReloadKeyRef = React.useRef(0);

  // What the fields open on when the form mounts; the effect below reloads
  // them from the entity when its cloud is set from outside.
  const opened = pointCloudInputsFor(entity.pointCloud);
  const [source, setSource] = React.useState(opened.source);
  const [axes, setAxes] = React.useState(opened.axes);
  const [color, setColor] = React.useState(opened.color);
  const [colorMode, setColorMode] = React.useState(opened.colorMode);
  const [gradientBy, setGradientBy] = React.useState(opened.gradient.by);
  const [colorLow, setColorLow] = React.useState(opened.gradient.colorLow);
  const [colorHigh, setColorHigh] = React.useState(opened.gradient.colorHigh);
  const [gradientMin, setGradientMin] = React.useState(
    `${opened.gradient.min ?? ""}`,
  );
  const [gradientMinError, setGradientMinError] = React.useState("");
  const [gradientMax, setGradientMax] = React.useState(
    `${opened.gradient.max ?? ""}`,
  );
  const [gradientMaxError, setGradientMaxError] = React.useState("");
  const [pointSize, setPointSize] = React.useState(`${opened.pointSize ?? ""}`);
  const [pointSizeError, setPointSizeError] = React.useState("");
  const [stride, setStride] = React.useState(`${opened.stride}`);
  const [strideError, setStrideError] = React.useState("");

  // The user picks a different file. A bound cloud is rebound to the topic of
  // the same name and `data` path in it, everything else kept, in one
  // dispatch. When the file holds no such topic the binding stands, and the
  // topic list is scoped to the file for the user to pick one of its own.
  const onFileChange = (newFileId: string) => {
    setScopingFile(newFileId);
    const cloud = entity.pointCloud;
    if (!cloud) {
      return;
    }
    const match = pathMatcher
      .findNodesByParts(
        cloud.source.topic.name,
        cloud.source.messagePath.parts,
        fileIdToTopics[newFileId] ?? [],
      )
      .find(isPointCloudDataPath);
    if (!match) {
      return;
    }
    // `isPointCloudDataPath` has checked the path has a representation the
    // worker reads, so `toTopicData` has one to bind.
    const rebound: PointCloud = { ...cloud, source: match.toTopicData() };

    // Match the form to what is dispatched; otherwise the effect below
    // rebuilds the cloud on the previous file's topic and dispatches that
    // over this one.
    setSource(rebound.source);
    pointCloudRef.current = rebound;
    topicReloadKeyRef.current++;
    dispatch(updateThreeDEntity(panelId, entity.id, { pointCloud: rebound }));
  };

  const presetId = presetIdFor(axes);

  React.useEffect(
    function onFieldsChanged() {
      const dispatchedOver = dispatchedOverRef.current;
      if (dispatchedOver) {
        if (isEqual(entity.pointCloud, dispatchedOver.cloud)) {
          // This form's dispatch has not landed yet; this runs again when it
          // does, and writes any edit made meanwhile.
          return;
        }
        dispatchedOverRef.current = undefined;
      }

      if (!isEqual(pointCloudRef.current, entity.pointCloud)) {
        // Differs from what this form last loaded or dispatched, so it was set
        // from outside: reload every field from it, scope the topic list to
        // its file again, and skip the rebuild below.
        pointCloudRef.current = entity.pointCloud;
        const loaded = pointCloudInputsFor(entity.pointCloud);
        setScopingFile(undefined);
        setSource(loaded.source);
        setAxes(loaded.axes);
        setColor(loaded.color);
        setColorMode(loaded.colorMode);
        setGradientBy(loaded.gradient.by);
        setColorLow(loaded.gradient.colorLow);
        setColorHigh(loaded.gradient.colorHigh);
        setGradientMin(`${loaded.gradient.min ?? ""}`);
        setGradientMax(`${loaded.gradient.max ?? ""}`);
        setPointSize(`${loaded.pointSize ?? ""}`);
        setStride(`${loaded.stride}`);
        topicReloadKeyRef.current++;
        return;
      }

      // An empty point size means the renderer's default, not an error.
      const parsedPointSize =
        pointSize.trim() === ""
          ? { ok: true as const, value: undefined }
          : parseNumberInput(pointSize, "Point size");
      const parsedStride = parseNumberInput(stride, "Stride");
      setPointSizeError(parsedPointSize.ok ? "" : parsedPointSize.error);
      setStrideError(parsedStride.ok ? "" : parsedStride.error);
      const parsedMin = parseRangeEnd(gradientMin, "Min");
      const parsedMax = parseRangeEnd(gradientMax, "Max");
      const rangeError =
        parsedMin.ok && parsedMax.ok
          ? validateGradientRange(parsedMin.value, parsedMax.value)
          : undefined;
      setGradientMinError(parsedMin.ok ? "" : parsedMin.error);
      setGradientMaxError(parsedMax.ok ? (rangeError ?? "") : parsedMax.error);
      if (!parsedPointSize.ok || !parsedStride.ok) {
        return;
      }
      const pointSizeValidationError =
        parsedPointSize.value === undefined
          ? undefined
          : validatePointSize(parsedPointSize.value);
      if (pointSizeValidationError) {
        setPointSizeError(pointSizeValidationError);
        return;
      }
      const strideValidationError = validateStride(parsedStride.value);
      if (strideValidationError) {
        setStrideError(strideValidationError);
        return;
      }

      // The range's fields only show in gradient mode, so an error in them
      // must not hold back a change made elsewhere — a switch to another mode
      // included. Until it is fixed, the range last saved stands.
      const range =
        parsedMin.ok && parsedMax.ok && !rangeError
          ? { min: parsedMin.value, max: parsedMax.value }
          : {
              min: entity.pointCloud?.gradient?.min,
              max: entity.pointCloud?.gradient?.max,
            };

      const updated = buildPointCloudSpec({
        source,
        axes,
        color,
        colorMode,
        gradient: { by: gradientBy, colorLow, colorHigh, ...range },
        pointSize: parsedPointSize.value,
        stride: parsedStride.value,
      });
      if (updated === null) {
        // No topic bound. An entity that still holds a cloud had its topic
        // cleared, so the cloud goes with it; one that holds none, as a new
        // entity does, has nothing to write.
        if (entity.pointCloud !== undefined) {
          pointCloudRef.current = undefined;
          dispatchedOverRef.current = { cloud: entity.pointCloud };
          dispatch(
            updateThreeDEntity(panelId, entity.id, { pointCloud: undefined }),
          );
        }
        return;
      }
      // Compared with what the form writes for the entity's cloud untouched
      // rather than with the cloud itself, so a cloud that only spells out a
      // default is not rewritten by being opened.
      const untouched = buildPointCloudSpec(
        pointCloudInputsFor(entity.pointCloud),
      );
      if (!isEqual(updated, untouched)) {
        pointCloudRef.current = updated;
        dispatchedOverRef.current = { cloud: entity.pointCloud };
        dispatch(
          updateThreeDEntity(panelId, entity.id, { pointCloud: updated }),
        );
      }
    },
    [
      source,
      axes,
      color,
      colorMode,
      gradientBy,
      colorLow,
      colorHigh,
      gradientMin,
      gradientMax,
      pointSize,
      stride,
      entity.pointCloud,
      entity.id,
      panelId,
      dispatch,
    ],
  );

  // Custom is offered only while it is what the axes are, as the position
  // picker offers its own: it names the current state, not a choice to make.
  const presetOptions: readonly PointCloudPresetOption[] =
    presetId === "custom"
      ? [...POINT_CLOUD_PRESETS, CUSTOM_PRESET_OPTION]
      : POINT_CLOUD_PRESETS;

  const onPresetChange = (value: string) => {
    const preset = POINT_CLOUD_PRESETS.find(
      (candidate) => candidate.id === value,
    );
    if (preset) {
      setAxes(axesOf(preset));
    }
  };

  return (
    <>
      <RobotoTypography variant="body2" className={styles.fieldLabel}>
        Point Cloud
      </RobotoTypography>
      <div className={styles.container}>
        {(availableFiles.length >= 2 || boundFileDangling) && (
          <ConfigItem label="File">
            <FileSelector
              className={styles.blockSpacing}
              files={availableFiles}
              selection={effectiveFileId}
              onSelect={onFileChange}
              isError={boundFileDangling}
              forceShow={boundFileDangling}
              helperText={
                boundFileDangling
                  ? "The file this point cloud was bound to is not in the current workspace. Pick a file and topic to continue."
                  : undefined
              }
            />
          </ConfigItem>
        )}
        <ConfigItem label="Topic">
          <PathSelector
            key={topicReloadKeyRef.current}
            className={styles.blockSpacing}
            initialInputValue={joinedPathFor(entity.pointCloud?.source)}
            onChange={(node) => setSource(node.toTopicData())}
            onClear={() => setSource(undefined)}
            options={topicTree}
            optionFilter={isPointCloudDataPath}
          />
        </ConfigItem>
        <ConfigItem label="Axes">
          <AxisPresetPicker
            className={styles.displayControl}
            options={presetOptions}
            value={presetOptions.find((option) => option.id === presetId)}
            onChange={(option) => onPresetChange(option.id)}
          />
        </ConfigItem>
        <ConfigItem label="Display">
          <FormControl
            size="small"
            fullWidth
            className={classNames(styles.field, styles.displayControl)}
          >
            <InputLabel
              id={`pc-color-mode-label-${entity.id}`}
              className={styles.input}
            >
              Color Mode
            </InputLabel>
            <Select
              label="Color Mode"
              labelId={`pc-color-mode-label-${entity.id}`}
              value={colorMode}
              onChange={(e) =>
                setColorMode(e.target.value as PointCloudColorMode)
              }
              className={styles.input}
              inputProps={{ className: styles.input }}
            >
              {COLOR_MODES.map((mode) => (
                <MenuItem key={mode.value} value={mode.value}>
                  {mode.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {colorMode === "gradient" && (
            <>
              <FormControl
                size="small"
                fullWidth
                className={classNames(styles.field, styles.displayControl)}
              >
                <InputLabel
                  id={`pc-gradient-by-label-${entity.id}`}
                  className={styles.input}
                >
                  Color By
                </InputLabel>
                <Select
                  label="Color By"
                  labelId={`pc-gradient-by-label-${entity.id}`}
                  value={gradientBy}
                  onChange={(e) =>
                    setGradientBy(e.target.value as PointCloudGradientMeasure)
                  }
                  className={styles.input}
                  inputProps={{ className: styles.input }}
                >
                  {GRADIENT_MEASURES.map((measure) => (
                    <MenuItem key={measure.value} value={measure.value}>
                      {measure.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <GradientColorFields
                colorLow={colorLow}
                colorHigh={colorHigh}
                onColorLowChange={setColorLow}
                onColorHighChange={setColorHigh}
              />
              <div className={styles.fieldRow}>
                <TextField
                  label="Min"
                  type="number"
                  size="small"
                  margin="dense"
                  placeholder="Auto"
                  value={gradientMin}
                  error={!!gradientMinError}
                  helperText={gradientMinError}
                  onChange={(e) => setGradientMin(e.target.value)}
                  InputLabelProps={{ shrink: true, className: styles.input }}
                  InputProps={{ className: styles.input }}
                  inputProps={{ className: styles.input }}
                  className={styles.scaleField}
                />
                <TextField
                  label="Max"
                  type="number"
                  size="small"
                  margin="dense"
                  placeholder="Auto"
                  value={gradientMax}
                  error={!!gradientMaxError}
                  helperText={gradientMaxError}
                  onChange={(e) => setGradientMax(e.target.value)}
                  InputLabelProps={{ shrink: true, className: styles.input }}
                  InputProps={{ className: styles.input }}
                  inputProps={{ className: styles.input }}
                  className={styles.scaleField}
                />
              </div>
            </>
          )}
          <div className={styles.fieldRow}>
            <TextField
              label="Point Size"
              type="number"
              size="small"
              margin="dense"
              value={pointSize}
              error={!!pointSizeError}
              helperText={pointSizeError}
              onChange={(e) => setPointSize(e.target.value)}
              InputLabelProps={{ className: styles.input }}
              InputProps={{ className: styles.input }}
              inputProps={{ min: 0, step: 1, className: styles.input }}
              className={styles.scaleField}
            />
            {/* Gradient mode never draws flat, so its flat color is moot. */}
            {colorMode !== "gradient" && (
              <TextField
                label="Color"
                type="color"
                size="small"
                margin="dense"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                InputLabelProps={{ className: styles.input }}
                InputProps={{ className: styles.input }}
                inputProps={{ className: styles.input }}
                className={styles.scaleField}
              />
            )}
            <TextField
              label="Stride"
              type="number"
              size="small"
              margin="dense"
              value={stride}
              error={!!strideError}
              helperText={strideError}
              onChange={(e) => setStride(e.target.value)}
              InputLabelProps={{ className: styles.input }}
              InputProps={{ className: styles.input }}
              inputProps={{ min: 1, step: 1, className: styles.input }}
              className={styles.scaleField}
            />
          </div>
        </ConfigItem>
      </div>
    </>
  );
}
